import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { galleryImages } from '../../data/productContent';
function GalleryPhoto({
  image,
  thumbnail = false
}: {
  image: typeof galleryImages[number];
  thumbnail?: boolean;
}) {
  if (image.crop) {
    const {
      x,
      y,
      width,
      height,
      sourceWidth,
      sourceHeight
    } = image.crop;
    return <svg role={thumbnail ? undefined : 'img'} aria-label={thumbnail ? undefined : image.alt} aria-hidden={thumbnail || undefined} viewBox={`${x} ${y} ${width} ${height}`} width={width} height={height} className={thumbnail ? 'h-full w-full' : 'h-auto max-h-full w-full'} style={thumbnail ? undefined : {
      maxWidth: `min(${image.nativeWidth}px, 100%)`
    }} data-id="element-268" data-mp="2z1rea6451v4f">
        <image href={image.src} width={sourceWidth} height={sourceHeight} data-id="element-269" data-mp="1ekbnkffwmzce" />
      </svg>;
  }
  return <img src={image.src} alt={thumbnail ? '' : image.alt} className={thumbnail ? 'h-full w-full object-cover' : 'max-h-full w-auto object-contain'} style={thumbnail ? undefined : {
    maxWidth: `min(${image.nativeWidth}px, 100%)`
  }} data-id="element-270" data-mp="1inhp59s2o2cj" />;
}
export function ProductGallery() {
  const [index, setIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const current = galleryImages[index];
  const go = (dir: number) => setIndex(i => (i + dir + galleryImages.length) % galleryImages.length);
  return <div role="region" aria-roledescription="gallery" aria-label="Product images" data-id="element-271" data-mp="2wmz5e64vcv87">
      <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-line bg-surface" data-id="element-272" data-mp="2rp7cp9weh52y">
        <AnimatePresence initial={false} data-id="element-273" data-mp="31fjs6npcy828">
          <motion.div key={current.id} initial={reduceMotion ? false : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-0 flex items-center justify-center p-6 md:p-10" data-id="element-274" data-mp="31guxwt2d5pvz">
            <GalleryPhoto image={current} data-id="element-275" data-mp="2d1wrlvod5815" />
          </motion.div>
        </AnimatePresence>
        <button type="button" onClick={() => go(-1)} aria-label="Previous image" className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-surface text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-276" data-mp="2cziw5lprttt2">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-277" data-mp="2vrlfj4tsro70" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next image" className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-surface text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-278" data-mp="30qy5hyfytbdt">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-279" data-mp="1afk1zogaorfo" />
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4" data-id="element-280" data-mp="12x3r68wlgbxo">
        <ul className="flex gap-3" aria-label="Choose image" data-id="element-281" data-mp="y4kdlbxwpgsv">
          {galleryImages.map((img, i) => <li key={img.id} data-id="element-282" data-mp="t6r9cog5ywv8" data-mpi={i}>
              <button type="button" onClick={() => setIndex(i)} aria-label={`Show ${img.label}`} aria-current={i === index ? 'true' : undefined} className={`block h-16 w-16 overflow-hidden rounded-[3px] border bg-surface transition-colors duration-150 ease-out ${i === index ? 'border-ink' : 'border-line hover:border-line-strong'}`} data-id="element-283" data-mp="t5ze5eqhb15h">
                <GalleryPhoto image={img} thumbnail data-id="element-284" data-mp="17tkfb33smqar" />
              </button>
            </li>)}
        </ul>
        <p aria-live="polite" className="hidden text-[13px] text-ink-muted sm:block" data-id="element-285" data-mp="1fbgico0kdwk">
          {current.label}
        </p>
      </div>
    </div>;
}